/**
 * SparkyFitness stores the same metric in different places depending on where
 * it came from (wearable daily summaries, phone check-ins, custom measurement
 * categories, exercise entries). This module turns those raw rows into one
 * value per day and metric.
 *
 * Rules:
 * - Cumulative metrics (steps, distance, calories, active minutes) take the
 *   highest value any single source reports for the day. Sources overlap (a
 *   watch and a phone both count the same walk), so they are never added.
 * - Workouts are counted from exercise entries only.
 * - Sleep takes the source with the most sleep that night.
 * - Resting heart rate prefers wearable daily summaries, then measurements.
 */
import {
  adherenceScore,
  epley1rm,
  todayIn,
  type ChallengeEvent,
  type MetricAggregate,
} from '@gauntlet/shared';
import type {
  CheckInRow,
  CustomCategory,
  CustomMeasurementRow,
  DailyHealthRow,
  ExerciseDayBucket,
  ExerciseEntryRow,
  ExerciseSetRow,
  FastRow,
  GoalsRow,
  NutritionDayRow,
  SleepRow,
  WaterRow,
} from './client.js';

export interface MergedValue {
  date: string;
  metric: string;
  value: number;
  source: string | null;
}

export interface MergeInput {
  start: string;
  end: string;
  daily: DailyHealthRow[];
  checkIns: CheckInRow[];
  /** Custom measurement rows keyed by category name. */
  custom: Record<string, { category: CustomCategory; rows: CustomMeasurementRow[] }>;
  exerciseDays: ExerciseDayBucket[];
  exerciseEntries: ExerciseEntryRow[];
  sleep: SleepRow[];
  /** Custom metrics wanted by the user's challenges: name (lower case) -> aggregate. */
  customMetrics: Record<string, MetricAggregate>;
  /** Food totals per day (days with food logged). */
  nutrition?: NutritionDayRow[];
  water?: WaterRow[];
  /** The person's goals per day, where known. Adherence is only scored on these days. */
  goals?: Record<string, GoalsRow>;
  fasts?: FastRow[];
  /** Exercises (lower case) wanted for per-exercise strength metrics. */
  lifts?: string[];
  /** The person's time zone, to put a fast on the day it ended. Defaults to UTC. */
  timezone?: string;
}

/** Custom categories SparkyFitness' own phone sync and integrations create for built-in metrics. */
export const DISTANCE_CATEGORIES = ['distance'];
export const RESTING_HR_CATEGORIES = [
  'resting_heart_rate',
  'resting heart rate',
  'restingheartrate',
];
export const ACTIVE_CALORIES_ENTRY = 'active calories';

export function wantedCategoryNames(customMetrics: Record<string, MetricAggregate>): string[] {
  return [...DISTANCE_CATEGORIES, ...RESTING_HR_CATEGORIES, ...Object.keys(customMetrics)];
}

/** Lower rank wins first; within a rank the highest value wins. */
type Candidate = { value: number; source: string | null; rank: number };

class DayTable {
  private readonly map = new Map<string, Map<string, Candidate[]>>();

  constructor(
    private readonly start: string,
    private readonly end: string,
  ) {}

  add(
    date: string | null | undefined,
    metric: string,
    value: unknown,
    source: string | null,
    rank = 0,
  ) {
    const d = normDate(date);
    const v = num(value);
    if (!d || d < this.start || d > this.end || v == null) return;
    let byMetric = this.map.get(d);
    if (!byMetric) this.map.set(d, (byMetric = new Map()));
    const list = byMetric.get(metric) ?? [];
    list.push({ value: v, source, rank });
    byMetric.set(metric, list);
  }

  collect(pick: (metric: string, candidates: Candidate[]) => Candidate | null): MergedValue[] {
    const out: MergedValue[] = [];
    for (const [date, byMetric] of this.map) {
      for (const [metric, candidates] of byMetric) {
        const chosen = pick(metric, candidates);
        if (chosen) out.push({ date, metric, value: round(chosen.value), source: chosen.source });
      }
    }
    return out.sort((a, b) =>
      a.date === b.date ? a.metric.localeCompare(b.metric) : a.date.localeCompare(b.date),
    );
  }
}

export function mergeDaily(input: MergeInput): MergedValue[] {
  const t = new DayTable(input.start, input.end);

  // Wearable daily summaries (Garmin, Polar, ...).
  for (const row of input.daily) {
    const src = row.source_provider ?? 'wearable';
    if (pos(row.total_steps)) t.add(row.entry_date, 'steps', row.total_steps, src);
    if (pos(row.total_distance_meters))
      t.add(row.entry_date, 'distance', num(row.total_distance_meters)! / 1000, src);
    if (pos(row.active_calories))
      t.add(row.entry_date, 'active_calories', row.active_calories, src);
    const minutes =
      num(row.exercise_minutes) ??
      (row.moderate_intensity_minutes != null || row.vigorous_intensity_minutes != null
        ? (num(row.moderate_intensity_minutes) ?? 0) + (num(row.vigorous_intensity_minutes) ?? 0)
        : null);
    if (pos(minutes)) t.add(row.entry_date, 'active_minutes', minutes, src);
    if (pos(row.resting_heart_rate))
      t.add(row.entry_date, 'resting_hr_daily', row.resting_heart_rate, src);
  }

  // Check-ins (phone sync, Fitbit, Withings). Newest row per day wins.
  const latestCheckIn = new Map<string, CheckInRow>();
  for (const row of input.checkIns) {
    const d = normDate(row.entry_date);
    if (!d) continue;
    const prev = latestCheckIn.get(d);
    if (!prev || (row.updated_at ?? '') > (prev.updated_at ?? '')) latestCheckIn.set(d, row);
  }
  for (const [d, row] of latestCheckIn) {
    if (pos(row.steps)) t.add(d, 'steps', row.steps, 'check-in');
  }

  // Custom measurement categories.
  for (const [name, { category, rows }] of Object.entries(input.custom)) {
    const key = name.toLowerCase();
    const label = category.display_name || category.name;
    if (DISTANCE_CATEGORIES.includes(key)) {
      for (const [d, v] of dailyCustom(rows, category, 'sum'))
        t.add(d, 'distance', v / 1000, label);
    } else if (RESTING_HR_CATEGORIES.includes(key)) {
      for (const [d, v] of dailyCustom(rows, category, 'avg'))
        t.add(d, 'resting_hr_measured', v, label);
    }
    const agg = input.customMetrics[key];
    if (agg) {
      for (const [d, v] of dailyCustom(rows, category, agg)) t.add(d, `custom:${key}`, v, label);
    }
  }

  // Workouts: counted from individual entries, with duplicates and stuck sessions removed.
  for (const [d, list] of cleanWorkouts(input.exerciseEntries)) {
    t.add(d, 'workouts', list.length, 'workouts');
    const minutes = list.reduce((a, w) => a + w.minutes, 0);
    if (minutes > 0) t.add(d, 'active_minutes', Math.min(minutes, 1440), 'workouts', 2);
    const kcal = list.reduce((a, w) => a + w.kcal, 0);
    if (kcal > 0) t.add(d, 'active_calories', kcal, 'workouts', 2);
  }
  // Workout distance comes from SparkyFitness' per-day exercise stats.
  for (const b of input.exerciseDays) {
    if (pos(b.distanceMeters)) t.add(b.startDate, 'distance', b.distanceMeters / 1000, 'workouts');
  }

  // Phone-synced whole-day active calories arrive as "Active Calories" exercise entries.
  const activeCal = new Map<string, number>();
  for (const e of input.exerciseEntries) {
    if ((e.exercise_name ?? '').trim().toLowerCase() !== ACTIVE_CALORIES_ENTRY) continue;
    const d = normDate(e.entry_date);
    const v = num(e.calories_burned);
    if (d && v != null) activeCal.set(d, (activeCal.get(d) ?? 0) + v);
  }
  for (const [d, v] of activeCal) if (v > 0) t.add(d, 'active_calories', v, 'Health Connect', 1);

  // Sleep: total per source per night, then the biggest source.
  const sleepBySource = new Map<string, number>();
  for (const s of input.sleep) {
    const d = normDate(s.entry_date);
    const asleep = num(s.time_asleep_in_seconds);
    const inBed = num(s.duration_in_seconds);
    // Some synced sessions report more time asleep than the session lasted.
    const secs = asleep && inBed ? Math.min(asleep, inBed) : asleep || inBed;
    if (!d || !secs) continue;
    const key = `${d}|${s.source ?? 'sleep'}`;
    sleepBySource.set(key, (sleepBySource.get(key) ?? 0) + secs);
  }
  for (const [key, secs] of sleepBySource) {
    const [d, src] = key.split('|') as [string, string];
    t.add(d, 'sleep', secs / 3600, src);
  }

  addNutrition(t, input);
  addStrength(t, input);
  addFasting(t, input);
  // Weigh-ins: newest check-in per day. Only ever used for % toward a person's own target.
  for (const [d, row] of latestCheckIn) {
    if (pos(row.weight)) t.add(d, 'weight', row.weight, 'check-in');
  }

  const merged = t.collect((metric, cands) => {
    if (metric === 'resting_hr_daily' || metric === 'resting_hr_measured') return cands[0] ?? null;
    if (metric.startsWith('custom:')) return cands[0] ?? null;
    return cands.reduce((best, c) =>
      c.rank < best.rank || (c.rank === best.rank && c.value > best.value) ? c : best,
    );
  });

  // Resting HR: wearable daily summary first, otherwise the measured category.
  const out: MergedValue[] = [];
  const restingDaily = new Set(
    merged.filter((m) => m.metric === 'resting_hr_daily').map((m) => m.date),
  );
  for (const m of merged) {
    if (m.metric === 'resting_hr_daily') out.push({ ...m, metric: 'resting_hr' });
    else if (m.metric === 'resting_hr_measured') {
      if (!restingDaily.has(m.date)) out.push({ ...m, metric: 'resting_hr' });
    } else out.push(m);
  }
  return out;
}

function addNutrition(t: DayTable, input: MergeInput) {
  const food = new Map<string, { calories: number; protein: number; carbs: number; fat: number }>();
  for (const row of input.nutrition ?? []) {
    const d = normDate(row.date);
    if (!d) continue;
    const day = {
      calories: num(row.calories) ?? 0,
      protein: num(row.protein) ?? 0,
      carbs: num(row.carbs) ?? 0,
      fat: num(row.fat) ?? 0,
    };
    if (!(day.calories > 0 || day.protein > 0 || day.carbs > 0 || day.fat > 0)) continue;
    food.set(d, day);
    t.add(d, 'calories_in', day.calories, 'food');
    t.add(d, 'protein', day.protein, 'food');
    t.add(d, 'carbs', day.carbs, 'food');
    t.add(d, 'fat', day.fat, 'food');
  }
  const water = new Map<string, number>();
  for (const row of input.water ?? []) {
    const d = normDate(row.entry_date);
    const ml = num(row.water_ml);
    if (d && ml && ml > 0) water.set(d, (water.get(d) ?? 0) + ml);
  }
  for (const [d, ml] of water) t.add(d, 'water', ml / 1000, 'water');

  for (const [d, g] of Object.entries(input.goals ?? {})) {
    const goal = {
      calories: num(g.calories),
      protein: num(g.protein),
      carbs: num(g.carbs),
      fat: num(g.fat),
      water: num(g.water_goal_ml),
    };
    if (pos(goal.calories)) t.add(d, 'goal_calories', goal.calories, 'goals');
    if (pos(goal.protein)) t.add(d, 'goal_protein', goal.protein, 'goals');
    if (pos(goal.carbs)) t.add(d, 'goal_carbs', goal.carbs, 'goals');
    if (pos(goal.fat)) t.add(d, 'goal_fat', goal.fat, 'goals');
    if (pos(goal.water)) t.add(d, 'goal_water', goal.water! / 1000, 'goals');

    // Adherence: only on days something was logged (no log scores nothing, i.e. 0 over a challenge).
    const eaten = food.get(d);
    if (eaten && eaten.calories > 0) {
      const cal = adherenceScore(eaten.calories, goal.calories);
      if (cal != null) t.add(d, 'calorie_adherence', cal, 'food');
    }
    if (eaten) {
      const macros = (['protein', 'carbs', 'fat'] as const)
        .map((k) => adherenceScore(eaten[k], goal[k]))
        .filter((v): v is number => v != null);
      if (macros.length)
        t.add(d, 'macro_adherence', macros.reduce((a, b) => a + b, 0) / macros.length, 'food');
    }
    const drank = water.get(d);
    if (drank && goal.water && goal.water > 0)
      t.add(d, 'water_goal', Math.min(100, (drank / goal.water) * 100), 'water');
  }
}

/** Warm-ups don't count toward strength metrics. */
function isWorkingSet(s: ExerciseSetRow): boolean {
  return !/warm/i.test(s.set_type ?? '') && (num(s.reps) ?? 0) > 0;
}

function addStrength(t: DayTable, input: MergeInput) {
  const wanted = new Set((input.lifts ?? []).map((l) => l.toLowerCase()));
  const volume = new Map<string, number>();
  const sets = new Map<string, number>();
  for (const e of input.exerciseEntries) {
    const d = normDate(e.entry_date);
    if (!d || !e.sets?.length) continue;
    const name = (e.exercise_name ?? '').trim().toLowerCase();
    for (const s of e.sets) {
      if (!isWorkingSet(s)) continue;
      const reps = num(s.reps)!;
      const kg = num(s.weight) ?? 0;
      sets.set(d, (sets.get(d) ?? 0) + 1);
      if (kg > 0) volume.set(d, (volume.get(d) ?? 0) + kg * reps);
      if (kg > 0 && wanted.has(name)) {
        t.add(d, `lift_max:${name}`, kg, e.exercise_source ?? 'workouts');
        t.add(d, `lift_e1rm:${name}`, epley1rm(kg, reps), e.exercise_source ?? 'workouts');
      }
    }
  }
  for (const [d, v] of volume) t.add(d, 'lift_volume', v, 'workouts');
  for (const [d, n] of sets) t.add(d, 'lift_sets', n, 'workouts');
}

function addFasting(t: DayTable, input: MergeInput) {
  const tz = input.timezone ?? 'UTC';
  const hours = new Map<string, number>();
  const done = new Map<string, number>();
  for (const f of input.fasts ?? []) {
    if ((f.status ?? '').toUpperCase() !== 'COMPLETED' || !f.end_time) continue;
    const end = new Date(f.end_time);
    const start = new Date(f.start_time);
    if (Number.isNaN(end.getTime()) || Number.isNaN(start.getTime())) continue;
    const mins = num(f.duration_minutes) ?? (end.getTime() - start.getTime()) / 60_000;
    if (!(mins > 0)) continue;
    const d = todayIn(tz, end);
    hours.set(d, (hours.get(d) ?? 0) + mins / 60);
    const target = f.target_end_time ? new Date(f.target_end_time) : null;
    if (!target || Number.isNaN(target.getTime()) || end.getTime() >= target.getTime() - 60_000)
      done.set(d, (done.get(d) ?? 0) + 1);
  }
  for (const [d, h] of hours) t.add(d, 'fast_hours', h, 'fasting');
  for (const [d, n] of done) t.add(d, 'fasts_completed', n, 'fasting');
}

/** A workout longer than this is a session someone forgot to stop. */
const MAX_WORKOUT_MINUTES = 6 * 60;
const MIN_WORKOUT_MINUTES = 3;

/**
 * Real workouts per day: drops SparkyFitness' "Active Calories" entries,
 * sessions too short or too long to be real, and duplicates: the same session
 * written by two apps, sometimes under different activity names, so it is
 * matched on near-identical duration and calories.
 */
export function cleanWorkouts(
  entries: ExerciseEntryRow[],
): Map<string, { name: string; minutes: number; kcal: number; source: string | null }[]> {
  const byDay = new Map<
    string,
    { name: string; minutes: number; kcal: number; source: string | null }[]
  >();
  for (const e of entries) {
    const name = (e.exercise_name ?? '').trim().toLowerCase();
    if (name === ACTIVE_CALORIES_ENTRY) continue;
    const d = normDate(e.entry_date);
    const minutes = num(e.duration_minutes) ?? 0;
    const kcal = num(e.calories_burned) ?? 0;
    if (!d || minutes < MIN_WORKOUT_MINUTES || minutes > MAX_WORKOUT_MINUTES) continue;
    const list = byDay.get(d) ?? [];
    const dup = list.some(
      (w) =>
        Math.abs(w.minutes - minutes) <= 2 && Math.abs(w.kcal - kcal) <= Math.max(5, kcal * 0.05),
    );
    if (!dup) list.push({ name, minutes, kcal, source: e.exercise_source ?? null });
    byDay.set(d, list);
  }
  for (const [d, list] of byDay) if (!list.length) byDay.delete(d);
  return byDay;
}

/** Reduce custom measurement rows to one value per day. */
function dailyCustom(
  rows: CustomMeasurementRow[],
  category: CustomCategory,
  aggregate: MetricAggregate,
): Map<string, number> {
  const groups = new Map<string, { value: number; ts: string }[]>();
  for (const r of rows) {
    const d = normDate(r.date);
    const v = num(r.value);
    if (!d || v == null) continue;
    const list = groups.get(d) ?? [];
    list.push({ value: v, ts: r.timestamp ?? String(r.hour ?? '') });
    groups.set(d, list);
  }
  const out = new Map<string, number>();
  const once = (category.frequency ?? '').toLowerCase() === 'daily';
  for (const [d, list] of groups) {
    if (once) {
      // One reading per day: the latest wins.
      out.set(d, list.reduce((a, b) => (b.ts > a.ts ? b : a)).value);
    } else {
      const sum = list.reduce((a, b) => a + b.value, 0);
      out.set(d, aggregate === 'sum' ? sum : sum / list.length);
    }
  }
  return out;
}

function normDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
}

function num(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

function pos(value: unknown): boolean {
  const n = num(value);
  return n != null && n > 0;
}

function round(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/** Exercises with weighted working sets, most-trained first (for the strength-challenge picker). */
export function liftOptions(
  entries: ExerciseEntryRow[],
): { key: string; label: string; sessions: number }[] {
  const seen = new Map<string, { label: string; sessions: number }>();
  for (const e of entries) {
    const label = (e.exercise_name ?? '').trim();
    const key = label.toLowerCase();
    if (!key || key.length > 60) continue;
    if (!e.sets?.some((s) => isWorkingSet(s) && (num(s.weight) ?? 0) > 0)) continue;
    const prev = seen.get(key);
    seen.set(key, { label: prev?.label ?? label, sessions: (prev?.sessions ?? 0) + 1 });
  }
  return [...seen]
    .map(([key, v]) => ({ key, ...v }))
    .sort((a, b) => b.sessions - a.sessions || a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------- events ("what counted")

const nf = (n: number, digits = 0) =>
  n.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
const titleCase = (s: string) => s.replace(/\b\p{L}/gu, (c) => c.toUpperCase());

/**
 * The workouts, lift sessions and finished fasts behind the daily values, built from the same
 * cleaned data (duplicates and stuck sessions removed, warm-ups left out), so what is listed is
 * exactly what scored.
 */
export function buildEvents(input: {
  start: string;
  end: string;
  exerciseEntries: ExerciseEntryRow[];
  fasts?: FastRow[];
  timezone?: string;
}): { date: string; event: ChallengeEvent }[] {
  const out: { date: string; event: ChallengeEvent }[] = [];
  const inWindow = (d: string) => d >= input.start && d <= input.end;

  for (const [date, list] of cleanWorkouts(input.exerciseEntries)) {
    if (!inWindow(date)) continue;
    list.forEach((w, i) => {
      const bits = [`${nf(w.minutes)} min`, w.kcal > 0 ? `${nf(w.kcal)} kcal` : ''].filter(Boolean);
      out.push({
        date,
        event: {
          id: `workout:${date}:${i}`,
          kind: 'workout',
          title: titleCase(w.name),
          detail: bits.join(' · '),
          source: w.source,
        },
      });
    });
  }

  let li = 0;
  for (const e of input.exerciseEntries) {
    const date = normDate(e.entry_date);
    if (!date || !inWindow(date)) continue;
    const sets = (e.sets ?? []).filter(isWorkingSet);
    if (!sets.length) continue;
    let top: { kg: number; reps: number } | null = null;
    let volume = 0;
    for (const s of sets) {
      const kg = num(s.weight) ?? 0;
      const reps = num(s.reps) ?? 0;
      volume += kg * reps;
      if (kg > 0 && (!top || kg > top.kg)) top = { kg, reps };
    }
    const bits = [
      `${sets.length} ${sets.length === 1 ? 'set' : 'sets'}`,
      top ? `top ${nf(top.kg, 1)} kg × ${nf(top.reps)}` : '',
      volume > 0 ? `${nf(volume)} kg volume` : '',
    ].filter(Boolean);
    out.push({
      date,
      event: {
        id: `lift:${date}:${li++}`,
        kind: 'lift',
        title: titleCase((e.exercise_name ?? 'Lift').trim()),
        detail: bits.join(' · '),
        source: e.exercise_source ?? null,
      },
    });
  }

  const tz = input.timezone ?? 'UTC';
  for (const f of input.fasts ?? []) {
    if ((f.status ?? '').toUpperCase() !== 'COMPLETED' || !f.end_time) continue;
    const end = new Date(f.end_time);
    const start = new Date(f.start_time);
    if (Number.isNaN(end.getTime()) || Number.isNaN(start.getTime())) continue;
    const date = todayIn(tz, end);
    if (!inWindow(date)) continue;
    const mins = num(f.duration_minutes) ?? (end.getTime() - start.getTime()) / 60_000;
    if (!(mins > 0)) continue;
    const target = f.target_end_time ? new Date(f.target_end_time) : null;
    const hit = !target || end.getTime() >= target.getTime() - 60_000;
    const targetH = target ? (target.getTime() - start.getTime()) / 3_600_000 : null;
    out.push({
      date,
      event: {
        id: `fast:${f.id}`,
        kind: 'fast',
        title: f.fasting_type ? `${f.fasting_type} fast` : 'Fast',
        detail: `${nf(mins / 60, 1)} h fasted${
          targetH ? ` · target ${nf(targetH, 1)} h${hit ? '' : ', ended early'}` : ''
        }`,
        source: null,
      },
    });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}
